import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Tag,
  Buildings,
  House,
  Wrench,
  Sliders,
} from '@phosphor-icons/react';
import { ReportDimension, ReportFieldMetadata } from 'shared/types/asset-report';
import { getEntityLabel } from '../constants';

interface PivotDrillDownMenuProps {
  anchorEl: HTMLElement;
  position: 'row' | 'column';
  fieldMetadata: ReportFieldMetadata[];
  usedFields: Set<string>;
  onSelect: (dim: ReportDimension) => void;
  onClose: () => void;
}

const DATE_GROUPINGS = [
  { key: 'year' as const, label: 'per jaar' },
  { key: 'quarter' as const, label: 'per kwartaal' },
  { key: 'month' as const, label: 'per maand' },
];

interface MenuItem {
  field: string;
  label: string;
  dateGrouping?: string;
}

interface MenuGroup {
  label: string;
  icon: React.ReactNode;
  items: MenuItem[];
}

const PivotDrillDownMenu: React.FC<PivotDrillDownMenuProps> = ({
  anchorEl,
  fieldMetadata,
  usedFields,
  onSelect,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  // Build clean menu groups matching the user's mental model
  const groups = useMemo((): MenuGroup[] => {
    const isAvailable = (f: string) => !usedFields.has(f);

    const makeDateItems = (fields: string[]): MenuItem[] =>
      fields.filter(isAvailable).flatMap(f =>
        DATE_GROUPINGS.map(dg => ({
          field: f,
          label: `${getEntityLabel(f, f)} ${dg.label}`,
          dateGrouping: dg.key,
        }))
      );

    const makeItems = (fields: string[]): MenuItem[] =>
      fields.filter(isAvailable).map(f => ({
        field: f,
        label: getEntityLabel(f, f),
      }));

    const result: MenuGroup[] = [
      {
        label: 'Voorzieningen',
        icon: <Tag className="h-3.5 w-3.5" />,
        items: [
          ...makeItems(['assetCategory.name', 'assetType.name', 'status']),
          ...makeDateItems(['installationDate', 'warrantyExpiryDate']),
        ],
      },
      {
        label: 'Gebouw',
        icon: <Buildings className="h-3.5 w-3.5" />,
        items: makeItems([
          'building.id',
          'building.googlePlaceLongAddress',
          'building.location.city.name',
          'building.location.city.zip',
          'building.location.city.province',
          'building.company.name',
        ]),
      },
      {
        label: 'Unit',
        icon: <House className="h-3.5 w-3.5" />,
        items: makeItems([
          'unit.id',
          'unit.unitType',
          'unit.unitSubtype',
        ]),
      },
      {
        label: 'Leverancier & Onderhoud',
        icon: <Wrench className="h-3.5 w-3.5" />,
        items: [
          ...makeItems(['installer.name']),
          ...makeDateItems(['lastMaintenanceDate', 'nextMaintenanceDate']),
        ],
      },
    ];

    // Dynamic fields from user-created category/type field definitions
    const dynamicItems = fieldMetadata
      .filter(f => f.category === 'dynamic' && !usedFields.has(f.field))
      .map(f => ({ field: f.field, label: f.label }));

    if (dynamicItems.length > 0) {
      result.push({
        label: 'Categorie- & type-specifieke velden',
        icon: <Sliders className="h-3.5 w-3.5" />,
        items: dynamicItems,
      });
    }

    return result;
  }, [fieldMetadata, usedFields]);

  // Filter by search
  const searchLower = search.toLowerCase();
  const filteredGroups = groups
    .map(g => ({
      ...g,
      items: g.items.filter(i => i.label.toLowerCase().includes(searchLower)),
    }))
    .filter(g => g.items.length > 0);

  // Position near the anchor
  const rect = anchorEl.getBoundingClientRect();
  const menuStyle: React.CSSProperties = {
    position: 'fixed',
    top: rect.bottom + 4,
    left: rect.left,
    zIndex: 50,
  };

  return (
    <div
      ref={menuRef}
      style={menuStyle}
      className="bg-popover border border-border rounded-lg shadow-lg w-[280px] overflow-hidden"
    >
      <div className="p-2 border-b">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek veld..."
          className="w-full px-2 py-1.5 text-sm bg-background border border-input rounded-md outline-none focus:ring-1 focus:ring-ring"
          autoFocus
        />
      </div>

      <div className="max-h-[320px] overflow-y-auto py-1">
        {filteredGroups.length === 0 ? (
          <p className="text-sm text-muted-foreground px-3 py-2">Geen velden beschikbaar</p>
        ) : (
          filteredGroups.map(group => (
            <div key={group.label}>
              <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {group.icon}
                {group.label}
              </div>
              {group.items.map((item, idx) => (
                <button
                  key={`${item.field}-${item.dateGrouping || ''}-${idx}`}
                  onClick={() => {
                    onSelect({
                      field: item.field,
                      label: item.label,
                      ...(item.dateGrouping ? { dateGrouping: item.dateGrouping as any } : {}),
                    });
                  }}
                  className="w-full text-left text-sm px-4 py-1.5 hover:bg-accent transition-colors text-foreground"
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PivotDrillDownMenu;
